import { useCallback, useMemo } from "react";

import { parseISO } from "date-fns";

import { useQuery } from "hooks";
import { createResolverFactory } from "components/QueryClient/ResolverFactory";

import toGlobalId from "scenes/Hubly/components/Workspace/api/helper";

export function useGetPracticeSubscription(practiceId, useGlobalId = true) {
  const queryFactory = useCallback(() => {
    const query = `
            query practiceSubscription {
              practice {
                subscriptions {
                  edges {
                    node {
                      id
                      uuid
                      status
                      nextBillingPrice
                      nextBillingAt
                      trialEnd
                      cancelledAt
                      upToDate
                      refundable
                      planOption {
                        name
                        pricing {
                          currency
                          monthlyPrice
                          billingPeriod
                        }
                        isInternal
                      }
                      plan {
                        name
                        featureList
                      }
                      scheduledChanges
                      scheduledChangesAt
                      hasScheduledPlanChanges
                    }
                  }
                }
                pendingChanges
              }
              me {
                isAccountOwner
              }
            }`;

    const resolver = ({ data: { practice, me } }) => {
      const subscription =
        practice?.subscriptions?.edges?.length > 0
          ? practice.subscriptions.edges[0].node
          : undefined;

      const parseToDate = (dateString) => {
        return dateString ? parseISO(dateString) : null;
      };

      return subscription
        ? {
            ...subscription,
            status: practice?.pendingChanges
              ? "Pending Changes"
              : subscription.status,
            id: useGlobalId ? subscription.id : subscription.uuid,
            nextBillingDate: parseToDate(subscription.nextBillingAt),
            scheduledChangesAt: parseToDate(subscription.scheduledChangesAt),
            trialEndDate: parseToDate(subscription.trialEnd),
            cancellationDate: parseToDate(subscription.cancelledAt),
            pendingChanges: practice?.pendingChanges
              ? "Pending Changes"
              : undefined,
            isAccountOwner: me?.isAccountOwner,
            isApiPartnerPlan: subscription?.planOption?.isInternal,
          }
        : [];
    };

    return { query: query, resolver: createResolverFactory(resolver) };
  }, [useGlobalId]);

  const { loading, data, error, numOfFetches, reloadData } = useQuery(
    queryFactory,
    {
      variables: useMemo(() => {
        return {
          practiceId: toGlobalId("Practice", practiceId),
        };
      }, [practiceId]),
    }
  );

  return {
    subscription: Array.isArray(data) && data?.length === 0 ? null : data,
    reloadData: reloadData,
    loading: !error && loading && numOfFetches === 0,
    isAccountOwner: data?.isAccountOwner,
    isApiPartnerPlan: data?.isApiPartnerPlan,
    disableChangePlanButton: [
      "Cancelled",
      "Pending Changes",
      "Non Renewing",
    ].includes(data?.status),
    error: error
      ? error[0]?.message ||
        "We could not load your subscription. Please retry or contact support for assistance"
      : "",
  };
}

export default useGetPracticeSubscription;
